import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import acceptedJobApi from '../../APIs/jobs';
import {
  Button,
  PageHeader,
  Pagination,
  Table,
  Tag,
  Typography,
} from '../../designComponents';
import { TableDataType } from '../../designComponents/Table';
import Store from '../../store';
import { RootState } from '../../store/selectors';
import theme from '../../theme';
import { PaginationType } from '../../types';
import { humanReadableDate, requestAPI } from '../../utils';
import { JobsContainerStyled } from './style';

type MyParamsType = PaginationType & { search?: string };

export type ContractType = {};

const Jobs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');

  const acceptedContractJobs = useSelector(
    (state: RootState) => state.acceptedContractJobs.data,
  );
  const pagination = useSelector(
    (state: RootState) => state.acceptedContractJobs.pagination,
  );

  const getAcceptedContract = async (myParams: MyParamsType) => {
    try {
      await requestAPI(
        acceptedJobApi.getAccepetedContractJobs({
          page: myParams.page,
          limit: myParams.limit,
          ...(myParams?.search ? { search: myParams.search } : {}),
        }),
      );
    } catch (error) {
      console.log('Error : ', error);
    }
  };

  const handlePagination = (updatedParams: MyParamsType) => {
    getAcceptedContract(updatedParams);
    dispatch(
      Store.Actions.update('acceptedContractList.pagination', updatedParams),
    );
  };

  const debouncedFunction = useCallback(
    debounce((myParams) => getAcceptedContract(myParams), 500),
    [],
  );

  useEffect(() => {
    getAcceptedContract(pagination);
  }, []);

  const handleOnSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    debouncedFunction({ ...pagination, page: 1, search: value });
  };

  const tableData: TableDataType = {
    headings: [
      { key: 'date', label: 'Date' },
      { key: 'jobTitle', label: 'Job Title' },
      { key: 'buildingName', label: 'Building Name' },
      { key: 'addressOfProperty', label: 'Address of Property' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Action' },
    ],
    body: Array.isArray(acceptedContractJobs)
      ? acceptedContractJobs?.map((jobAndProposal) => {
        return {
          id: jobAndProposal._id,
          date: humanReadableDate(jobAndProposal.contract.createdAt),
          jobTitle: jobAndProposal.technicianJob.jobTitle,
          buildingName: jobAndProposal.building.buildingName,
          addressOfProperty: jobAndProposal.building.buildingAddress,
          status: (
              <Tag type="success">{jobAndProposal?.technicianJob?.status}</Tag>
          ),
          actions: (
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    `details/${jobAndProposal._id}/jobs/${jobAndProposal.technicianJob._id}`,
                  )
                }
                style={{
                  backgroundColor: theme.colors.blueLight,
                  border: `1px solid ${theme.colors.blueLight}`,
                }}
              >
                View Details
              </Button>
          ),
        };
      })
      : [],
  };

  return (
    <JobsContainerStyled>
      <PageHeader
        pageTitle={<Typography variant="title">Jobs</Typography>}
        search={search}
        handleOnSearchTextChange={handleOnSearchTextChange}
      />
      <div className="table-container">
        <Table tableData={tableData} />
      </div>

      <Pagination
        activePage={pagination.page}
        handlePagination={handlePagination}
        limit={pagination.limit}
        totalCount={pagination.totalData || 0}
      />
    </JobsContainerStyled>
  );
};

export default Jobs;
